import React, { useEffect, useMemo, useState } from "react";
import { Button, deleteIconButtonClass } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { rolesAPI, usuariosAPI, type Usuario } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, X } from "lucide-react";

interface RolesUsuariosPanelProps {
  rolCodigo: string;
  rolNombre: string;
  onRoleGone?: () => void;
  onBusyChange?: (busy: boolean) => void;
}

function apiErrorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== "object" || !("status" in error)) return undefined;
  const status = (error as { status?: unknown }).status;
  return typeof status === "number" ? status : undefined;
}

function splitBulkTokens(raw: string): { committed: string[]; rest: string } {
  const parts = raw.split(/[,;\n]+/);
  if (parts.length <= 1) return { committed: [], rest: raw };
  const rest = parts.pop() ?? "";
  return { committed: parts.map((s) => s.trim()).filter(Boolean), rest };
}

export function RolesUsuariosPanel({
  rolCodigo,
  rolNombre,
  onRoleGone,
  onBusyChange,
}: RolesUsuariosPanelProps) {
  const [loading, setLoading] = useState(true);
  const [listed, setListed] = useState<Usuario[]>([]);
  const [allUsers, setAllUsers] = useState<Usuario[]>([]);
  const [picked, setPicked] = useState<string[]>([]);
  const [pickMove, setPickMove] = useState<string[]>([]);
  const [query, setQuery] = useState("");
  const [bulkQuery, setBulkQuery] = useState("");
  const [bulkList, setBulkList] = useState<string[]>([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<string[] | null>(null);
  const onRoleGoneRef = React.useRef(onRoleGone);
  const onBusyChangeRef = React.useRef(onBusyChange);
  onRoleGoneRef.current = onRoleGone;
  onBusyChangeRef.current = onBusyChange;

  const markBusy = (next: boolean) => {
    setBusy(next);
    onBusyChangeRef.current?.(next);
  };

  const load = async (): Promise<boolean> => {
    setLoading(true);
    try {
      const [onRole, everyone] = await Promise.all([
        rolesAPI.listUsuarios(rolCodigo),
        usuariosAPI.list(),
      ]);
      setListed(onRole);
      setAllUsers(everyone);
      return true;
    } catch (error) {
      if (apiErrorStatus(error) === 404) {
        onRoleGoneRef.current?.();
        return false;
      }
      console.error(error);
      toast.error("Error al cargar usuarios del rol");
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const boot = async () => {
      setLoading(true);
      try {
        const [onRole, everyone] = await Promise.all([
          rolesAPI.listUsuarios(rolCodigo),
          usuariosAPI.list(),
        ]);
        if (cancelled) return;
        setListed(onRole);
        setAllUsers(everyone);
      } catch (error) {
        if (cancelled) return;
        if (apiErrorStatus(error) === 404) {
          onRoleGoneRef.current?.();
          return;
        }
        console.error(error);
        toast.error("Error al cargar usuarios del rol");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void boot();
    return () => {
      cancelled = true;
    };
  }, [rolCodigo]);

  const onRoleUsernames = useMemo(
    () => new Set(listed.map((u) => u.username)),
    [listed],
  );
  const others = useMemo(
    () => allUsers.filter((u) => !onRoleUsernames.has(u.username)),
    [allUsers, onRoleUsernames],
  );
  const q = query.trim().toLowerCase();
  const suggestions = useMemo(() => {
    if (!q) return [];
    return others
      .filter(
        (u) =>
          u.username.toLowerCase().includes(q) ||
          `${u.nombre} ${u.apellido}`.toLowerCase().includes(q),
      )
      .slice(0, 6);
  }, [others, q]);

  const displayName = (u: Usuario) =>
    `${u.nombre || ""} ${u.apellido || ""}`.trim() || u.username;

  const bulkTaken = useMemo(
    () => new Set([...onRoleUsernames, ...bulkList]),
    [onRoleUsernames, bulkList],
  );
  const bulkCandidates = useMemo(
    () => others.filter((u) => !bulkTaken.has(u.username)),
    [others, bulkTaken],
  );
  const bq = bulkQuery.trim().toLowerCase();
  const bulkSuggestions = useMemo(() => {
    if (!bq) return [];
    return bulkCandidates
      .filter(
        (u) =>
          u.username.toLowerCase().includes(bq) ||
          `${u.nombre} ${u.apellido}`.toLowerCase().includes(bq),
      )
      .slice(0, 6);
  }, [bulkCandidates, bq]);

  const matchUser = (token: string, pool: Usuario[]): Usuario | undefined => {
    const t = token.trim().toLowerCase();
    if (!t) return undefined;
    return (
      pool.find((u) => u.username.toLowerCase() === t) ||
      pool.find((u) => displayName(u).toLowerCase() === t) ||
      pool.find(
        (u) =>
          u.username.toLowerCase().includes(t) ||
          displayName(u).toLowerCase().includes(t),
      )
    );
  };

  const addToBulkList = (usernames: string[]) => {
    const incoming = usernames.filter(
      (id) => id && !onRoleUsernames.has(id) && !bulkList.includes(id),
    );
    if (!incoming.length) return false;
    setBulkList((prev) => [...prev, ...incoming.filter((id) => !prev.includes(id))]);
    return true;
  };

  const commitBulkTokens = (tokens: string[]): boolean => {
    const found: string[] = [];
    for (const token of tokens) {
      const hit = matchUser(token, bulkCandidates.filter((u) => !found.includes(u.username)));
      if (hit) found.push(hit.username);
    }
    return addToBulkList(found);
  };

  const consumeBulkInput = (value: string) => {
    const { committed, rest } = splitBulkTokens(value);
    if (committed.length) commitBulkTokens(committed);
    setBulkQuery(rest);
  };

  const addBulkFromTypeahead = () => {
    const pick = bulkSuggestions[0] || matchUser(bulkQuery, bulkCandidates);
    if (!pick) {
      if (bulkQuery.trim()) setNote("Bulk: ninguna coincidencia en el padrón.");
      return;
    }
    addToBulkList([pick.username]);
    setBulkQuery("");
    setNote("");
  };

  const moveUsers = async (usernames: string[]) => {
    const incoming = usernames.filter((id) => !onRoleUsernames.has(id));
    if (!incoming.length) return;
    markBusy(true);
    try {
      const result = await rolesAPI.reassignUsers(rolCodigo, incoming);
      const fromOther = (result.moved || []).filter(
        (m) => m.rolAnterior && m.rolAnterior !== rolNombre,
      );
      if (fromOther.length) {
        const sample = fromOther
          .slice(0, 3)
          .map((u) => `${u.nombre || u.username} (${u.rolAnterior} → ${rolNombre})`)
          .join("; ");
        setNote(
          `Movidos ${fromOther.length} a ${rolNombre}. ${sample}${fromOther.length > 3 ? "…" : ""}`,
        );
      } else {
        setNote(`Asignados ${incoming.length} a ${rolNombre}.`);
      }
      setQuery("");
      setPickMove((prev) => prev.filter((id) => !incoming.includes(id)));
      setBulkList((prev) => prev.filter((id) => !incoming.includes(id)));
      toast.success(
        incoming.length === 1
          ? "Usuario movido a este rol"
          : `${incoming.length} usuarios movidos a este rol`,
      );
      await load();
    } catch (error: unknown) {
      if (apiErrorStatus(error) === 404) {
        onRoleGoneRef.current?.();
        return;
      }
      console.error(error);
      toast.error(error instanceof Error ? error.message : "No se pudo reasignar");
    } finally {
      markBusy(false);
    }
  };

  const applyBulk = () => {
    const leftover = bulkQuery.trim()
      ? matchUser(bulkQuery, bulkCandidates)?.username
      : undefined;
    const names = leftover ? [...bulkList, leftover] : bulkList;
    if (!names.length) {
      setNote("Bulk: agregá usuarios con Tab, Enter, coma o punto y coma.");
      return;
    }
    setBulkQuery("");
    void moveUsers(names);
  };

  const confirmRemove = async () => {
    if (!removeTarget?.length) return;
    markBusy(true);
    try {
      await rolesAPI.removeUsers(rolCodigo, removeTarget);
      toast.success(
        removeTarget.length === 1
          ? "Usuario quitado de este rol"
          : "Usuarios quitados de este rol",
      );
      setPicked([]);
      setRemoveTarget(null);
      setNote("Quedaron sin rol hasta que se les asigne otro.");
      await load();
    } catch (error: unknown) {
      if (apiErrorStatus(error) === 404) {
        onRoleGoneRef.current?.();
        return;
      }
      console.error(error);
      toast.error(error instanceof Error ? error.message : "No se pudo quitar");
    } finally {
      markBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium">Usuarios con este rol</h3>
        <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-primary/10 px-2 text-xs font-semibold text-primary">
          {listed.length}
        </span>
      </div>

      <div className="space-y-2">
        {listed.length === 0 ? (
          <p className="text-xs text-muted-foreground">Nadie tiene este rol todavía.</p>
        ) : (
          listed.map((u) => (
            <div key={u.username} className="flex items-center gap-2">
              <Checkbox
                checked={picked.includes(u.username)}
                onCheckedChange={(on) =>
                  setPicked(
                    on
                      ? [...picked, u.username]
                      : picked.filter((x) => x !== u.username),
                  )
                }
              />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium leading-tight">{displayName(u)}</p>
                <p className="text-xs text-muted-foreground">{u.username}</p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className={`h-8 w-8 p-0 ${deleteIconButtonClass}`}
                disabled={busy}
                title="Quitar"
                aria-label={`Quitar ${displayName(u)} de este rol`}
                onClick={() => setRemoveTarget([u.username])}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))
        )}
      </div>

      {picked.length > 0 && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={() => setRemoveTarget(picked)}
        >
          Quitar selección ({picked.length})
        </Button>
      )}

      <div className="border-t border-border pt-3 space-y-2">
        <p className="text-sm font-medium">Agregar - Individual</p>
        <p className="text-xs text-muted-foreground">
          Buscar y mover a este rol. Deja el anterior.
        </p>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar usuario o nombre…"
          className="w-full px-3 py-2 border border-input bg-background rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
        />
        {suggestions.map((u) => (
          <div key={u.username} className="flex items-center justify-between gap-2 py-1">
            <div className="min-w-0">
              <p className="text-sm">
                {displayName(u)} ({u.username})
              </p>
              <p className="text-xs text-muted-foreground">
                Hoy: {u.rol || "sin rol"} → se mueve a {rolNombre}
              </p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              disabled={busy}
              title="Agregar a este rol"
              aria-label={`Agregar ${displayName(u)} a este rol`}
              onClick={() => void moveUsers([u.username])}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>

      {others.length > 0 && (
        <div className="border-t border-border pt-3 space-y-2">
          <p className="text-sm font-medium">Agregar - Múltiples</p>
          <div className="max-h-48 space-y-1 overflow-y-auto pr-1">
            {others.map((u) => (
              <div key={u.username} className="flex items-center gap-2">
                <Checkbox
                  checked={pickMove.includes(u.username)}
                  onCheckedChange={(on) =>
                    setPickMove(
                      on
                        ? [...pickMove, u.username]
                        : pickMove.filter((x) => x !== u.username),
                    )
                  }
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium leading-tight">{displayName(u)}</p>
                  <p className="text-xs text-muted-foreground">
                    {u.username} · hoy {u.rol || "sin rol"}
                  </p>
                </div>
              </div>
            ))}
          </div>
          {pickMove.length > 0 && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() => void moveUsers(pickMove)}
            >
              <Plus className="h-4 w-4" />
              Selección ({pickMove.length})
            </Button>
          )}
        </div>
      )}

      <div className="border-t border-border pt-3 space-y-2">
        <p className="text-sm font-medium">Agregar - Bulk</p>
        <div className="flex min-h-[2.5rem] flex-wrap items-center gap-1 rounded-lg border border-input bg-background px-2 py-1.5">
          {bulkList.map((username) => {
            const u = allUsers.find((x) => x.username === username);
            return (
              <span
                key={username}
                className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs"
              >
                {u ? displayName(u) : username}
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={() => setBulkList((prev) => prev.filter((x) => x !== username))}
                  aria-label={`Quitar ${username}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
          <input
            type="text"
            value={bulkQuery}
            onChange={(e) => consumeBulkInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Tab" || e.key === "Enter") {
                if (bulkQuery.trim() || bulkSuggestions.length) {
                  e.preventDefault();
                  addBulkFromTypeahead();
                }
              } else if (e.key === "Backspace" && !bulkQuery && bulkList.length) {
                e.preventDefault();
                setBulkList((prev) => prev.slice(0, -1));
              }
            }}
            placeholder="Buscar y agregar (Tab o Enter). También , o ;"
            className="min-w-[8rem] flex-1 bg-transparent px-1 py-1 text-sm outline-none"
          />
        </div>
        {bulkSuggestions.map((u) => (
          <button
            key={u.username}
            type="button"
            className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-1 text-left hover:bg-muted"
            onClick={() => {
              addToBulkList([u.username]);
              setBulkQuery("");
            }}
          >
            <span className="text-sm">
              {displayName(u)} ({u.username})
            </span>
            <span className="text-xs text-muted-foreground">hoy {u.rol || "sin rol"}</span>
          </button>
        ))}
        <Button type="button" variant="secondary" size="sm" disabled={busy} onClick={applyBulk}>
          <Plus className="h-4 w-4" />
          Todos
        </Button>
        {note ? <p className="text-xs text-muted-foreground">{note}</p> : null}
      </div>

      <AlertDialog open={!!removeTarget} onOpenChange={(open) => !open && setRemoveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Quitar del rol</AlertDialogTitle>
            <AlertDialogDescription>
              {removeTarget?.length === 1
                ? "Este usuario queda sin rol (sin módulos en el menú) hasta que se le asigne otro. ¿Continuar?"
                : `${removeTarget?.length || 0} usuarios quedan sin rol (sin módulos) hasta que se les asigne otro. ¿Continuar?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={() => void confirmRemove()}>
              Quitar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
